import React, { useState, useEffect } from "react";
import Modal from "../ui/Modal";
import { GetPromotionPlan, ExecutePromotion, NotifyTeams } from "../../global/Apis";
import { useMsal, useAccount } from "@azure/msal-react";
import PromotionPlanSummaryBar from "../ui/PromotionPlanSummaryBar";

interface PreviewPlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectKey: string;
  fixVersionId: string;
  fixVersionName: string;
}

type PromotionTarget = "stg" | "prod";

type PromotionPlanSummary = {
    total_repos: number;
    tickets_in_fix_version: string[];
    tickets_not_in_fix_version: string[];
    unticketed_commits: number;
    tickets_with_no_commits: string[];
};

type PromotionPlanResponse = {
  org_name: string;
  promotion_target: PromotionTarget;
  project_key: string;
  fix_version_id: string;
  tickets_requested: string[];
  tickets_used: string[];
  repos: Array<{
    repo: string;
    source_branch: string;
    target_branch: string;
    status: string;
    reason?: string | null;
    commits_in_fix_version: Commit[];
    commits_other_ticket: Commit[];
    commits_unticketed: Commit[];
  }>;
  summary: PromotionPlanSummary;
};

type Commit = {
  repo_name: string;
  branch_name: string;
  jira_ticket: string;
  commit_sha: string;
  message: string;
  author: string;
  committed_at: string;
  category: string;
};

const categoryColor = (category: string) => {
  switch (category) {
    case "IN_FIX_VERSION":
      return "bg-green-100 border-green-400 text-green-900";
    case "TICKET_NOT_IN_FIX_VERSION":
      return "bg-orange-100 border-orange-400 text-orange-900";
    case "UNTICKETED":
      return "bg-red-100 border-red-400 text-red-900";
    default:
      return "bg-gray-100 border-gray-300 text-gray-900";
  }
};

const repoHasActionableChanges = (repo: PromotionPlanResponse["repos"][number]) =>
  repo.status === "has_changes";

const getRepoStatusLabel = (status: string) => {
  switch (status) {
    case "no_changes_already_in_target":
      return "No changes to promote";
    case "source_branch_missing":
      return "Source branch missing";
    case "target_branch_missing":
      return "Target branch missing";
    case "compare_failed":
      return "Compare failed";
    case "has_changes":
      return "Ready";
    default:
      return "Unavailable";
  }
};

const getRepoStatusClasses = (status: string) => {
  switch (status) {
    case "no_changes_already_in_target":
      return "bg-gray-50 border-gray-200 text-gray-700";
    case "source_branch_missing":
    case "target_branch_missing":
      return "bg-amber-50 border-amber-200 text-amber-800";
    case "compare_failed":
      return "bg-red-50 border-red-200 text-red-800";
    default:
      return "bg-gray-50 border-gray-200 text-gray-700";
  }
};

const CommitRow: React.FC<{
  commit: Commit & { _type: string; _idx: number };
  repoName: string;
  orgName: string;
  checked: boolean;
  onChange: () => void;
}> = ({ commit, repoName, orgName, checked, onChange }) => {
  const githubUrl = `https://github.com/${orgName}/${repoName}/commit/${commit.commit_sha}`;
  return (
    <div className={`grid grid-cols-[24px_80px_1fr_120px_140px] gap-3 items-start border-t py-2 px-3 ${categoryColor(commit.category)}`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="mt-0.5"
      />
      <a href={githubUrl} target="_blank" rel="noopener noreferrer" className="font-mono text-xs hover:underline truncate" onClick={e => e.stopPropagation()}>
        {commit.commit_sha.slice(0, 7)}
      </a>
      <span className="text-xs whitespace-normal break-words">
        {commit.message || <span className="text-gray-400 italic">No message</span>}
      </span>
      <span className="text-xs truncate" title={commit.author}>{commit.author}</span>
      <span className="text-xs truncate">{new Date(commit.committed_at).toLocaleString()}</span>
    </div>
  );
};

const PreviewPlanModal: React.FC<PreviewPlanModalProps> = ({ isOpen, onClose, projectKey, fixVersionId, fixVersionName }) => {
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  const [target, setTarget] = useState<PromotionTarget | "">("");
  const [isLoading, setIsLoading] = useState(false);
  const [plan, setPlan] = useState<PromotionPlanResponse | null>(null);
  const [expandedRepo, setExpandedRepo] = useState<string | null>(null);
  const [expandedTickets, setExpandedTickets] = useState<Record<string, boolean>>({});
  const [selectedCommits, setSelectedCommits] = useState<{ [key: string]: boolean }>({});
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'plan' | 'promotion' | 'pullrequests'>('plan');
  const [promotionResult, setPromotionResult] = useState<any>(null);
  const [isPromoting, setIsPromoting] = useState(false);
  const [expandedPRRepos, setExpandedPRRepos] = useState<Record<string, boolean>>({});
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});
  const [strategy, setStrategy] = useState<string>("INTERMEDIATE");
  const [excludedRepos, setExcludedRepos] = useState<Set<string>>(new Set());
  const [isSendingToTeams, setIsSendingToTeams] = useState(false);
  const [teamsSent, setTeamsSent] = useState(false);
  const [teamsError, setTeamsError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
        setPromotionResult(null);
        setTeamsSent(false);
        setTeamsError(null);
        setIsSendingToTeams(false);
        setActiveTab('plan');
        setExpandedPRRepos({});
        setPlan(null);
        setTarget("");
        setSelectedCommits({});
        setExpandedRepo(null);
        setExpandedTickets({});
        setExpandedCategories({});
        setExcludedRepos(new Set());
        setError(null);
    }
}, [isOpen]);

  const handleRun = async () => {
    if (!target) return;
    setIsLoading(true);
    setError(null);
    // Reset everything from previous promotion
    setPromotionResult(null);
    setTeamsSent(false);
    setTeamsError(null);
    setIsSendingToTeams(false);
    setActiveTab('plan');
    setExpandedPRRepos({});
    setPlan(null);
    setSelectedCommits({});
    setExpandedRepo(null);
    setExpandedTickets({});
    setExpandedCategories({});
    setExcludedRepos(new Set());
    try {
      const data = await GetPromotionPlan(instance, account, projectKey, fixVersionId, target);
      setPlan(data);
      setExcludedRepos(new Set());
      // Preselect IN_FIX_VERSION commits
      const preselected: { [key: string]: boolean } = {};
      data.repos.forEach((repo: any) => {
        [
          ...repo.commits_in_fix_version,
          ...repo.commits_other_ticket,
          ...repo.commits_unticketed
        ].forEach((commit: any) => {
          const cKey = `${repo.repo}:${commit.commit_sha}`;
          if (commit.category === "IN_FIX_VERSION") preselected[cKey] = true;
        });
      });
      setSelectedCommits(preselected);
    } catch (e: any) {
      setError(e.message || "Failed to fetch promotion plan");
      setPlan(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckbox = (repoName: string, sha: string) => {
    const cKey = `${repoName}:${sha}`;
    setSelectedCommits(prev => ({ ...prev, [cKey]: !prev[cKey] }));
  };

  const actionableRepos = plan
    ? plan.repos.filter(repo => repoHasActionableChanges(repo) && !excludedRepos.has(repo.repo))
    : [];

  const informationalRepos = plan
    ? plan.repos.filter(repo => !repoHasActionableChanges(repo))
    : [];

  const hasAnySelection = actionableRepos.some(repo => {
    const allCommits = [
      ...(repo.commits_in_fix_version || []),
      ...(repo.commits_other_ticket || []),
      ...(repo.commits_unticketed || [])
    ];
    return allCommits.some(c => selectedCommits[`${repo.repo}:${c.commit_sha}`]);
  });

  const handlePromote = async () => {
    if (!plan) return;
    setIsPromoting(true);
    setError(null);
    try {
      const repo_selections = actionableRepos.map(repo => {
        const allCommits = [
          ...(repo.commits_in_fix_version || []),
          ...(repo.commits_other_ticket || []),
          ...(repo.commits_unticketed || [])
        ];
        const selectedForRepo = allCommits
          .filter(c => selectedCommits[`${repo.repo}:${c.commit_sha}`])
          .sort((a, b) => new Date(a.committed_at).getTime() - new Date(b.committed_at).getTime());
        return {
          repo: repo.repo,
          source_branch: repo.source_branch,
          target_branch: repo.target_branch,
          selected_commits: selectedForRepo,
        };
      }).filter(entry => entry.selected_commits.length > 0);
      const payload = {
        org_name: plan.org_name || 'outthinkthreats',
        promotion_target: plan.promotion_target,
        project_key: plan.project_key,
        fix_version_id: plan.fix_version_id,
        fix_version_name: fixVersionName || '',
        strategy: strategy,
        repo_selections,
      };
      const result = await ExecutePromotion(instance, account, payload);
      setPromotionResult(result);
      setActiveTab('pullrequests');
    } catch (e: any) {
      setError(e.message || 'Failed to execute promotion');
    } finally {
      setIsPromoting(false);
    }
  };

  const handleSendToTeams = async () => {
      if (!promotionResult?.teams_message_preview) return;
      setIsSendingToTeams(true);
      setTeamsError(null);
      try {
          await NotifyTeams(instance, account, {
              message: promotionResult.teams_message_preview,
              promotion_target: promotionResult.promotion_target,
              fix_version_name: fixVersionName,
          });
          setTeamsSent(true);
      } catch (e: any) {
          setTeamsError(e.message || "Failed to send to Teams");
      } finally {
          setIsSendingToTeams(false);
      }
  };

  

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Preview Promotion Plan" width="max-w-7xl">
      <>
        {/* Tabs */}
        <div className="flex justify-center mb-4">
          <button
            className={`px-6 py-2 rounded-t-lg font-semibold text-sm transition-all duration-200 border-b-2 focus:outline-none mr-2 ${
              activeTab === 'plan' ? 'bg-indigo-50 border-indigo-500 text-indigo-700 shadow' : 'bg-gray-50 border-transparent text-gray-500 hover:bg-gray-100'
            }`}
            onClick={() => setActiveTab('plan')}
          >
            Plan
          </button>
          <button
            className={`px-6 py-2 rounded-t-lg font-semibold text-sm transition-all duration-200 border-b-2 focus:outline-none ml-2 ${
              activeTab === 'promotion' ? 'bg-indigo-50 border-indigo-500 text-indigo-700 shadow' : 'bg-gray-50 border-transparent text-gray-500 hover:bg-gray-100'
            }`}
            onClick={() => setActiveTab('promotion')}
          >
            Promotion
          </button>
          <button
            className={`px-6 py-2 rounded-t-lg font-semibold text-sm transition-all duration-200 border-b-2 focus:outline-none ml-2 ${
              activeTab === 'pullrequests' ? 'bg-indigo-50 border-indigo-500 text-indigo-700 shadow' : 'bg-gray-50 border-transparent text-gray-500 hover:bg-gray-100'
            }`}
            onClick={() => setActiveTab('pullrequests')}
            disabled={!promotionResult}
          >
            Pull Requests
          </button>
        </div>

        {/* Controls (target + run) stay visible regardless of tab */}
        {activeTab === 'plan' && (
          <div className="flex items-center gap-4 mb-6">
            <select
              className="border border-indigo-200 rounded-md px-3 py-2 text-sm focus:ring-indigo-400 focus:border-indigo-400 bg-white"
              value={target}
              onChange={e => {
                setTarget(e.target.value as PromotionTarget);
                if (e.target.value === "stg") {
                  setStrategy("INTERMEDIATE");
                }
              }}
            >
              <option value="">Select Target</option>
              <option value="stg">stg</option>
              <option value="prod">prod</option>
            </select>
            {target === "prod" && (
              <div className="flex items-center gap-2 ml-2">
                <label className="font-semibold mr-2">Strategy:</label>
                <label className="flex items-center gap-1">
                  <input
                    type="radio"
                    name="strategy"
                    value="INTERMEDIATE"
                    checked={strategy === "INTERMEDIATE"}
                    onChange={() => setStrategy("INTERMEDIATE")}
                  />
                  Intermediate
                </label>
                <label className="flex items-center gap-1">
                  <input
                    type="radio"
                    name="strategy"
                    value="DIRECT"
                    checked={strategy === "DIRECT"}
                    onChange={() => setStrategy("DIRECT")}
                  />
                  Direct
                </label>
              </div>
            )}
            <button
              className={`px-4 py-2 rounded-md text-white text-sm font-medium ${target ? "bg-indigo-600 hover:bg-indigo-700" : "bg-gray-300 cursor-not-allowed"}`}
              disabled={!target || isLoading}
              onClick={handleRun}
            >
              {isLoading ? "Running..." : "Run"}
            </button>
          </div>
        )}
        {error && <div className="text-red-600 mb-4">{error}</div>}

        {/* Plan Tab */}
        {activeTab === 'plan' && plan && (
          <div className="space-y-4">
            {/* Summary Bar */}
              {plan.summary && (
                <PromotionPlanSummaryBar summary={plan.summary} />
              )}
              {/* Repo list */}
              {plan.repos.map((repo, repoIdx) => (
                <div key={repo.repo + '-' + repoIdx} className="border rounded-lg">
                  <button
                    className={`w-full text-left px-4 py-2 font-semibold rounded-t-lg flex justify-between items-center transition-colors ${
                      !repoHasActionableChanges(repo)
                        ? 'bg-gray-50 hover:bg-gray-100'
                        : excludedRepos.has(repo.repo)
                          ? 'bg-gray-100 hover:bg-gray-200'
                          : 'bg-indigo-50 hover:bg-indigo-100'
                    }`}
                    onClick={() => setExpandedRepo(expandedRepo === repo.repo ? null : repo.repo)}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={repoHasActionableChanges(repo) && !excludedRepos.has(repo.repo)}
                        disabled={!repoHasActionableChanges(repo)}
                        onChange={e => {
                          e.stopPropagation();
                          if (!repoHasActionableChanges(repo)) return;

                          setExcludedRepos(prev => {
                            const next = new Set(prev);
                            next.has(repo.repo) ? next.delete(repo.repo) : next.add(repo.repo);
                            return next;
                          });
                        }}
                        className={`w-4 h-4 accent-indigo-600 ${repoHasActionableChanges(repo) ? "cursor-pointer" : "cursor-not-allowed opacity-50"}`}
                      />
                      <span className={excludedRepos.has(repo.repo) ? 'text-gray-400 line-through' : ''}>
                        {repo.repo}
                      </span>
                    </div>
                    <span className="text-xs text-gray-500">{expandedRepo === repo.repo ? "▲" : "▼"}</span>
                  </button>
                {expandedRepo === repo.repo && (
                  <div className={`p-4 bg-white space-y-4 ${excludedRepos.has(repo.repo) ? 'opacity-40 pointer-events-none' : ''}`}>
                    {!repoHasActionableChanges(repo) ? (
                      <div className={`border rounded-md px-4 py-3 ${getRepoStatusClasses(repo.status)}`}>
                        <div className="font-semibold">{getRepoStatusLabel(repo.status)}</div>

                        {repo.reason && (
                          <div className="text-sm mt-1">{repo.reason}</div>
                        )}

                        <div className="text-xs mt-2 opacity-80">
                          {repo.source_branch} → {repo.target_branch}
                        </div>
                      </div>
                    ) : (
                      <>
                        {/* Optional: show delta_shas count */}
                        {Array.isArray((repo as any).delta_shas) && (repo as any).delta_shas.length > 0 && (
                          <div className="text-xs text-gray-500 mb-2">Delta commits: {(repo as any).delta_shas.length}</div>
                        )}

                        {/* Category: IN_FIX_VERSION */}
                        {(() => {
                          const commits = (repo.commits_in_fix_version || []).map((commit, idx) => ({
                            ...commit,
                            _type: 'in_fix',
                            _idx: idx
                          }));
                          if (commits.length === 0) return null;

                          const catKey = `${repo.repo}-cat-infix`;
                          const isCatOpen = !!expandedCategories[catKey];
                          const byTicket: Record<string, typeof commits> = {};

                          commits.forEach(c => {
                            const key = c.jira_ticket || 'UNTICKETED';
                            if (!byTicket[key]) byTicket[key] = [];
                            byTicket[key].push(c);
                          });

                          return (
                            <div className="border rounded-md">
                              <button
                                className="w-full text-left px-3 py-2 bg-green-50 hover:bg-green-100 font-semibold rounded-t-md flex justify-between items-center"
                                onClick={() => setExpandedCategories(prev => ({ ...prev, [catKey]: !isCatOpen }))}
                              >
                                <span>In Fix Version</span>
                                <span className="text-xs text-green-700">{isCatOpen ? '▲' : '▼'}</span>
                              </button>

                              {isCatOpen && (
                                <div className="p-2">
                                  {Object.entries(byTicket)
                                    .sort((a, b) => {
                                      const latestA = Math.max(...a[1].map(c => new Date(c.committed_at).getTime()));
                                      const latestB = Math.max(...b[1].map(c => new Date(c.committed_at).getTime()));
                                      return latestB - latestA;
                                    })
                                    .map(([ticket, tCommits], idx) => {
                                      const ticketKey = `${repo.repo}-infix-${ticket}-${idx}`;
                                      const isOpen = !!expandedTickets[ticketKey];
                                      const allChecked = tCommits.every(c => selectedCommits[`${repo.repo}:${c.commit_sha}`]);
                                      const someChecked = tCommits.some(c => selectedCommits[`${repo.repo}:${c.commit_sha}`]);

                                      return (
                                        <div key={ticketKey} className="border rounded-md mb-3">
                                          <button
                                            className="w-full text-left px-3 py-2 bg-gray-50 hover:bg-gray-100 font-medium rounded-t-md flex justify-between items-center"
                                            onClick={() => setExpandedTickets(prev => ({ ...prev, [ticketKey]: !isOpen }))}
                                          >
                                            <span className="truncate mr-2">{ticket}</span>
                                            <span className="text-xs text-gray-500">{isOpen ? '▲' : '▼'}</span>
                                          </button>

                                          <div className="flex items-center gap-2 px-3 py-2 border-t bg-white">
                                            <input
                                              type="checkbox"
                                              checked={allChecked}
                                              onChange={() => {
                                                setSelectedCommits(prev => {
                                                  const next = { ...prev };
                                                  tCommits.forEach(c => {
                                                    next[`${repo.repo}:${c.commit_sha}`] = !allChecked;
                                                  });
                                                  return next;
                                                });
                                              }}
                                            />
                                            <span className="text-xs text-gray-600">Select all for ticket</span>
                                            {someChecked && !allChecked && (
                                              <span className="text-[10px] text-gray-400">(partial)</span>
                                            )}
                                          </div>

                                          {isOpen && (
                                            <div className="bg-white">
                                              {tCommits
                                                .slice()
                                                .sort((a, b) => new Date(b.committed_at).getTime() - new Date(a.committed_at).getTime())
                                                .map(commit => (
                                                  <CommitRow
                                                    key={commit.commit_sha + '-' + commit._type + '-' + commit._idx}
                                                    commit={commit}
                                                    repoName={repo.repo}
                                                    orgName={plan?.org_name || 'outthinkthreats'}
                                                    checked={!!selectedCommits[`${repo.repo}:${commit.commit_sha}`]}
                                                    onChange={() => handleCheckbox(repo.repo, commit.commit_sha)}
                                                  />
                                                ))}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                </div>
                              )}
                            </div>
                          );
                        })()}

                        {/* Category: TICKET_NOT_IN_FIX_VERSION */}
                        {(() => {
                          const commits = (repo.commits_other_ticket || []).map((commit, idx) => ({
                            ...commit,
                            _type: 'other',
                            _idx: idx
                          }));
                          if (commits.length === 0) return null;

                          const catKey = `${repo.repo}-cat-other`;
                          const isCatOpen = !!expandedCategories[catKey];
                          const byTicket: Record<string, typeof commits> = {};

                          commits.forEach(c => {
                            const key = c.jira_ticket || 'UNTICKETED';
                            if (!byTicket[key]) byTicket[key] = [];
                            byTicket[key].push(c);
                          });

                          return (
                            <div className="border rounded-md">
                              <button
                                className="w-full text-left px-3 py-2 bg-amber-50 hover:bg-amber-100 font-semibold rounded-t-md flex justify-between items-center"
                                onClick={() => setExpandedCategories(prev => ({ ...prev, [catKey]: !isCatOpen }))}
                              >
                                <span>Ticket Not In Fix Version</span>
                                <span className="text-xs text-amber-700">{isCatOpen ? '▲' : '▼'}</span>
                              </button>

                              {isCatOpen && (
                                <div className="p-2">
                                  {Object.entries(byTicket)
                                    .sort((a, b) => {
                                      const latestA = Math.max(...a[1].map(c => new Date(c.committed_at).getTime()));
                                      const latestB = Math.max(...b[1].map(c => new Date(c.committed_at).getTime()));
                                      return latestB - latestA;
                                    })
                                    .map(([ticket, tCommits], idx) => {
                                      const ticketKey = `${repo.repo}-other-${ticket}-${idx}`;
                                      const isOpen = !!expandedTickets[ticketKey];
                                      const allChecked = tCommits.every(c => selectedCommits[`${repo.repo}:${c.commit_sha}`]);
                                      const someChecked = tCommits.some(c => selectedCommits[`${repo.repo}:${c.commit_sha}`]);

                                      return (
                                        <div key={ticketKey} className="border rounded-md mb-3">
                                          <button
                                            className="w-full text-left px-3 py-2 bg-gray-50 hover:bg-gray-100 font-medium rounded-t-md flex justify-between items-center"
                                            onClick={() => setExpandedTickets(prev => ({ ...prev, [ticketKey]: !isOpen }))}
                                          >
                                            <span className="truncate mr-2">{ticket}</span>
                                            <span className="text-xs text-gray-500">{isOpen ? '▲' : '▼'}</span>
                                          </button>

                                          <div className="flex items-center gap-2 px-3 py-2 border-t bg-white">
                                            <input
                                              type="checkbox"
                                              checked={allChecked}
                                              onChange={() => {
                                                setSelectedCommits(prev => {
                                                  const next = { ...prev };
                                                  tCommits.forEach(c => {
                                                    next[`${repo.repo}:${c.commit_sha}`] = !allChecked;
                                                  });
                                                  return next;
                                                });
                                              }}
                                            />
                                            <span className="text-xs text-gray-600">Select all for ticket</span>
                                            {someChecked && !allChecked && (
                                              <span className="text-[10px] text-gray-400">(partial)</span>
                                            )}
                                          </div>

                                          {isOpen && (
                                            <div className="bg-white">
                                              {tCommits
                                                .slice()
                                                .sort((a, b) => new Date(b.committed_at).getTime() - new Date(a.committed_at).getTime())
                                                .map(commit => (
                                                  <CommitRow
                                                    key={commit.commit_sha + '-' + commit._type + '-' + commit._idx}
                                                    commit={commit}
                                                    repoName={repo.repo}
                                                    orgName={plan?.org_name || 'outthinkthreats'}
                                                    checked={!!selectedCommits[`${repo.repo}:${commit.commit_sha}`]}
                                                    onChange={() => handleCheckbox(repo.repo, commit.commit_sha)}
                                                  />
                                                ))}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                </div>
                              )}
                            </div>
                          );
                        })()}

                        {/* Category: UNTICKETED */}
                        {(() => {
                          const commits = (repo.commits_unticketed || []).map((commit, idx) => ({
                            ...commit,
                            _type: 'unticketed',
                            _idx: idx
                          }));
                          if (commits.length === 0) return null;

                          const catKey = `${repo.repo}-cat-unticketed`;
                          const isCatOpen = !!expandedCategories[catKey];
                          const byTicket: Record<string, typeof commits> = {};

                          commits.forEach(c => {
                            const key = c.jira_ticket || 'UNTICKETED';
                            if (!byTicket[key]) byTicket[key] = [];
                            byTicket[key].push(c);
                          });

                          return (
                            <div className="border rounded-md">
                              <button
                                className="w-full text-left px-3 py-2 bg-red-50 hover:bg-red-100 font-semibold rounded-t-md flex justify-between items-center"
                                onClick={() => setExpandedCategories(prev => ({ ...prev, [catKey]: !isCatOpen }))}
                              >
                                <span>Unticketed</span>
                                <span className="text-xs text-red-700">{isCatOpen ? '▲' : '▼'}</span>
                              </button>

                              {isCatOpen && (
                                <div className="p-2">
                                  {Object.entries(byTicket)
                                    .sort((a, b) => {
                                      const latestA = Math.max(...a[1].map(c => new Date(c.committed_at).getTime()));
                                      const latestB = Math.max(...b[1].map(c => new Date(c.committed_at).getTime()));
                                      return latestB - latestA;
                                    })
                                    .map(([ticket, tCommits], idx) => {
                                      const ticketKey = `${repo.repo}-unticketed-${ticket}-${idx}`;
                                      const isOpen = !!expandedTickets[ticketKey];
                                      const allChecked = tCommits.every(c => selectedCommits[`${repo.repo}:${c.commit_sha}`]);
                                      const someChecked = tCommits.some(c => selectedCommits[`${repo.repo}:${c.commit_sha}`]);

                                      return (
                                        <div key={ticketKey} className="border rounded-md mb-3">
                                          <button
                                            className="w-full text-left px-3 py-2 bg-gray-50 hover:bg-gray-100 font-medium rounded-t-md flex justify-between items-center"
                                            onClick={() => setExpandedTickets(prev => ({ ...prev, [ticketKey]: !isOpen }))}
                                          >
                                            <span className="truncate mr-2">{ticket}</span>
                                            <span className="text-xs text-gray-500">{isOpen ? '▲' : '▼'}</span>
                                          </button>

                                          <div className="flex items-center gap-2 px-3 py-2 border-t bg-white">
                                            <input
                                              type="checkbox"
                                              checked={allChecked}
                                              onChange={() => {
                                                setSelectedCommits(prev => {
                                                  const next = { ...prev };
                                                  tCommits.forEach(c => {
                                                    next[`${repo.repo}:${c.commit_sha}`] = !allChecked;
                                                  });
                                                  return next;
                                                });
                                              }}
                                            />
                                            <span className="text-xs text-gray-600">Select all for ticket</span>
                                            {someChecked && !allChecked && (
                                              <span className="text-[10px] text-gray-400">(partial)</span>
                                            )}
                                          </div>

                                          {isOpen && (
                                            <div className="bg-white">
                                              {tCommits
                                                .slice()
                                                .sort((a, b) => new Date(b.committed_at).getTime() - new Date(a.committed_at).getTime())
                                                .map(commit => (
                                                  <CommitRow
                                                    key={commit.commit_sha + '-' + commit._type + '-' + commit._idx}
                                                    commit={commit}
                                                    repoName={repo.repo}
                                                    orgName={plan?.org_name || 'outthinkthreats'}
                                                    checked={!!selectedCommits[`${repo.repo}:${commit.commit_sha}`]}
                                                    onChange={() => handleCheckbox(repo.repo, commit.commit_sha)}
                                                  />
                                                ))}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
        </div>
      )}

      {/* Promotion Tab */}
      {activeTab === 'promotion' && plan && (
        <div className="bg-white rounded-md border">
          <div className="overflow-x-auto">
            {actionableRepos.length === 0 ? (
              <div className="p-8 text-center text-gray-400 italic">
                No repos selected. Go to the Plan tab to include repos.
              </div>
            ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Repo</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source Branch</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Target Branch</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Selected Commits</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {actionableRepos.map((repo) =>  {
                  // Collect selected commits for this repo across all categories
                  const allCommits = [
                    ...(repo.commits_in_fix_version || []).map(c => ({ ...c, _type: 'in_fix' })),
                    ...(repo.commits_other_ticket || []).map(c => ({ ...c, _type: 'other' })),
                    ...(repo.commits_unticketed || []).map(c => ({ ...c, _type: 'unticketed' }))
                  ];
                  const selectedForRepo = allCommits
                    .filter(c => selectedCommits[`${repo.repo}:${c.commit_sha}`])
                    .sort((a, b) => new Date(a.committed_at).getTime() - new Date(b.committed_at).getTime());

                  return (
                    <tr key={`promotion-${repo.repo}`}>
                      <td className="px-4 py-2 text-sm text-gray-700">{repo.repo}</td>
                      <td className="px-4 py-2 text-sm text-gray-700">{repo.source_branch}</td>
                      <td className="px-4 py-2 text-sm text-gray-700">{repo.target_branch}</td>
                      <td className="px-4 py-2 text-sm text-gray-700">
                        {selectedForRepo.length === 0 ? (
                          <span className="text-gray-400">None selected</span>
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            {selectedForRepo.map(c => (
                              <span key={`${repo.repo}:${c.commit_sha}`} className="px-2 py-1 bg-indigo-50 text-indigo-700 rounded border border-indigo-200 font-mono text-xs">
                                {(c.jira_ticket || 'UNTICKETED') + ' - ' + c.commit_sha.slice(0, 7)}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            )}
          </div>
          {informationalRepos.length > 0 && (
              <div className="mt-4 border rounded-md bg-gray-50 p-4">
                <div className="font-semibold text-sm text-gray-700 mb-2">Repos not eligible for promotion</div>
                <div className="space-y-2">
                  {informationalRepos.map(repo => (
                    <div key={`info-${repo.repo}`} className="text-sm text-gray-600">
                      <span className="font-medium">{repo.repo}</span>
                      <span className="mx-2">-</span>
                      <span>{getRepoStatusLabel(repo.status)}</span>
                      {repo.reason ? <span className="text-gray-500">: {repo.reason}</span> : null}
                    </div>
                  ))}
                </div>
              </div>
            )}
        </div>
      )}

      {/* Bottom Promote Button */}
      {activeTab === 'promotion' && (
        <div className="mt-6 flex justify-end">
          <button
            className={`px-6 py-2 rounded-md text-white text-sm font-medium ${hasAnySelection && !isPromoting ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-300 cursor-not-allowed'}`}
            disabled={!hasAnySelection || isPromoting}
            onClick={handlePromote}
          >
            {isPromoting ? 'Promoting...' : 'Promote'}
          </button>
        </div>
      )}

      {/* Pull Requests Tab */}
      {activeTab === 'pullrequests' && promotionResult && (
    <div className="space-y-4">

        {/* Teams message preview */}
        {promotionResult.teams_message_preview && (
            <div className="border border-indigo-100 rounded-lg p-4 bg-indigo-50">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-indigo-700 text-sm">Release Comms Preview</h3>
                    <div className="flex items-center gap-2">
                        {teamsError && (
                            <span className="text-xs text-red-600">{teamsError}</span>
                        )}
                        {teamsSent ? (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-green-100 text-green-800 border border-green-300">
                                ✓ Sent to Teams
                            </span>
                        ) : (
                            <button
                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium text-white ${
                                    isSendingToTeams ? "bg-gray-400 cursor-not-allowed" : "bg-indigo-600 hover:bg-indigo-700"
                                }`}
                                disabled={isSendingToTeams}
                                onClick={handleSendToTeams}
                            >
                                {isSendingToTeams ? "Sending..." : "Send to Teams"}
                            </button>
                        )}
                    </div>
                </div>
                <pre className="text-xs text-gray-700 whitespace-pre-wrap font-mono bg-white border border-indigo-100 rounded p-3 max-h-64 overflow-y-auto">
                    {promotionResult.teams_message_preview}
                </pre>
            </div>
        )}

        {/* PR results */}
        {promotionResult.results && promotionResult.results.length > 0 ? (
            promotionResult.results.map((pr: any, idx: number) => {
                const repoKey = pr.repo + '-' + idx;
                const isOpen = !!expandedPRRepos[repoKey];
                return (
                    <div key={repoKey} className="border rounded-lg">
                        <button
                            className="w-full text-left px-4 py-2 bg-indigo-50 hover:bg-indigo-100 font-semibold rounded-t-lg flex justify-between items-center"
                            onClick={() => setExpandedPRRepos(prev => ({ ...prev, [repoKey]: !isOpen }))}
                        >
                            <span>{pr.repo}</span>
                            <div className="flex items-center gap-2">
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                    pr.status === "CREATED" ? "bg-green-100 text-green-800" :
                                    pr.status === "FAILED" ? "bg-red-100 text-red-800" :
                                    pr.status === "ALREADY_EXISTS" ? "bg-blue-100 text-blue-800" :
                                    "bg-gray-100 text-gray-700"
                                }`}>{pr.status}</span>
                                <span className="text-xs text-gray-500">{isOpen ? "▲" : "▼"}</span>
                            </div>
                        </button>
                        {isOpen && (
                            <div className="p-4 bg-white space-y-2">
                                <div className="flex flex-col gap-2 text-sm">
                                    <div><b>PR Number:</b> {pr.pr_number || "-"}</div>
                                    <div><b>PR URL:</b> {pr.pr_url ? <a href={pr.pr_url} className="text-blue-600 underline" target="_blank" rel="noopener noreferrer">{pr.pr_url}</a> : "-"}</div>
                                    {pr.intermediate_branch && <div><b>Intermediate Branch:</b> {pr.intermediate_branch}</div>}
                                    {pr.selected_count > 0 && <div><b>Selected Count:</b> {pr.selected_count}</div>}
                                    {pr.error && <div className="text-red-600"><b>Error:</b> {pr.error}</div>}
                                </div>
                            </div>
                        )}
                    </div>
                );
            })
        ) : (
            <div className="text-gray-500">No pull requests created.</div>
        )}
    </div>
)}
      </>
    </Modal>
  );
};

export default PreviewPlanModal;
